import { toast } from 'sonner';
import type { FlightCarrierSegment, FlightSearchResponse } from '@/types/flightSearch';
import type { CompareRequestBody, FlightSliceSummary, RouteComparisonResponse } from '@/types/compare';
import type { SearchOverviewApiResponse, SearchOverviewRequestBody } from '@/types/searchOverview';
import type { CashComparisonFreshness } from '@/lib/cashFreshness';
import type { AlternateBookingPath } from '@/types/alternateBookingPaths';
import type { AwardAccess } from '@/lib/awardAccess';
import type { AwardDecisionSnapshot } from '@/types/awardDecision';
import type { PassengerMix } from '@/lib/passengerMix';
import type {
  AdminBlogBackfillResult,
  AdminBlogMetrics,
  AdminBlogMetricsQuery,
  AdminBlogPost,
  AdminBlogPostInput,
  AdminBlogRegenerateResult,
  BlogListQuery,
  BlogPostDetail,
  BlogPostListResponse,
  BlogReactions,
  BlogReactionValue,
  BlogSubscribeResponse,
  BlogSubscribeSource,
} from '@/types/blog';
import {
  type ProductAnalyticsAction,
  type RecordProductAnalyticsActionOptions,
} from '@/lib/productAnalytics';

const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000').replace(/\/$/, '');
const parsePositiveTimeout = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 30_000 ? parsed : fallback;
};
export const FLIGHT_SEARCH_TIMEOUT_MS = parsePositiveTimeout(
  import.meta.env.VITE_FLIGHT_SEARCH_TIMEOUT_MS,
  180_000,
);

export type ConfidenceTier = 'Chart Verified' | 'Estimated';
export type PricingStatus = 'PRICED' | 'NO_CANDIDATES' | 'UNAVAILABLE';
export type PricingQualifier = 'EXACT' | 'ESTIMATED' | 'TYPICAL';
export type CppRating = 'excellent' | 'good' | 'fair' | 'poor';
export type CashPricingScope = 'LEG_TOTAL' | 'TOTAL_TRIP' | 'OFFER_TOTAL' | 'UNKNOWN';
export type TransferPathType = 'DIRECT_CARD_TRANSFER' | 'PARTNER_BOOKING_PATH' | 'NO_DIRECT_TRANSFER';
export type ReachableCreditCardProgram =
  | string
  | {
      programName?: string | null;
      name?: string | null;
      displayName?: string | null;
      code?: string | null;
      programCode?: string | null;
      transferRatio?: string | null;
      ratio?: string | null;
      transferUrl?: string | null;
      programUrl?: string | null;
      url?: string | null;
      deepLinkUrl?: string | null;
    };

export type SaverPricingOption = {
  rank: number;
  programCode: string;
  programName: string;
  programDisplayName?: string | null;
  programOwnerAirlineIata?: string | null;
  programOwnerAirlineName?: string | null;
  transferPathType?: TransferPathType | null;
  bookingInstruction?: string | null;
  recommendationConsistencyReason?: string | null;
  transferRatio: string;
  matchedProgramCode: string | null;
  matchedOperatingAirlineIata: string | null;
  milesPerPerson: number;
  totalMiles: number;
  milesMedian: number | null;
  chartFloor: number | null;
  // POINTS-412: the program noun (miles vs points) for explicit mileage display.
  pointsUnit?: string | null;
  confidenceTier: ConfidenceTier;
  evidenceSource?: 'chart' | 'third_party' | null;
  pricingStatus: 'PRICED';
  pricingQualifier: PricingQualifier;
  cpp: number | null;
  cppDisplay: string | null;
  cppRating: CppRating | null;
  cppRateUsed: number | null;
  tier: 1 | 2 | 3;
  label: string;
  confidence: 'high' | 'medium' | 'low';
  isBootstrap: boolean;
  redemptionScore: number | null;
  hasTransferBonus: boolean;
  transferBonus: { bonusPercentage: number; effectiveTransfer: number } | null;
  directTransferPartners?: ReachableCreditCardProgram[];
  reachableCreditCardPrograms?: ReachableCreditCardProgram[];
  alternateBookingPaths?: AlternateBookingPath[];
  isReachableWithSavedCreditCards?: boolean;
  isReachableWithSelectedCreditCard?: boolean;
  // POINTS-195/196: award chart source and freshness metadata (chart-backed labels only).
  awardChartSource?: string | null;
  awardChartLastVerifiedAt?: string | null;
  awardChartProvenance?: 'award_charts' | null;
  /** POINTS-487/486: supplementary backend honesty flags for UI callouts. */
  pricingHonestyFlags?: readonly string[] | null;
};

export interface CashComparison {
  label: 'Cash Comparison';
  source: 'DUFFEL';
  amount: number;
  baseAmount?: number;
  totalAmount: number;
  perPersonAmount: number;
  taxAmount: number;
  currency: string;
  pricingScope: CashPricingScope;
  passengerCount: number;
  cabinClass: string;
  requestedCabinClass: string;
  actualCabinClass: string;
  isRoundTrip: boolean;
  sliceCount: number;
  legRole: 'ONE_WAY' | 'OUTBOUND' | 'RETURN';
  timestamp: string;
  cpp: number | null;
  cppDisplay: string | null;
  cppRating: CppRating | null;
  // POINTS-189/190: Duffel expiry freshness metadata for honest Cash Comparison states.
  expiresAt?: string;
  freshness?: CashComparisonFreshness;
  refreshRequired?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
  deviceToken?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // POINTS-386: ISO `YYYY-MM-DD` birthday, required at registration.
  dateOfBirth: string;
}

export interface AuthResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    status: string;
  };
  message?: string;
  deviceToken?: string;
}

export type LoginResponse = AuthResponse | { requiresOtp: true; otpToken: string };

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  membershipTier: string | null;
  points: number;
  createdAt: string | null;
  phoneNumber?: string;
  // POINTS-386: ISO `YYYY-MM-DD` birthday, or null when not set.
  dateOfBirth?: string | null;
  preferences?: Record<string, unknown>;
  avatar?: string;
  // POINTS theme preference (#408). Returned top-level by /auth/profile and /users/profile/me.
  themePreference?: 'light' | 'dark';
}

export interface SearchRequestBody {
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  cabin?: 'economy' | 'premium_economy' | 'business' | 'first';
  passengers?: number;
  passengerMix?: PassengerMix;
  filters?: {
    maxMiles?: number;
    minCpp?: number;
    airlines?: string[];
    maxStops?: number;
  };
  sort?: 'cpp' | 'miles' | 'score';
}

export interface AirportSearchResult {
  iata: string;
  name: string;
  city: string;
  country: string;
  iataCityCode: string | null;
}

// POINTS-82: two-step independent outbound/return selection
export interface FlightOffer {
  id: string;
  flightNumber: string;
  operatingCarrier: string;
  operatingCarrierIata: string;
  milesPerPerson: number;
  totalMiles: number;
  confidenceTier: ConfidenceTier;
  evidenceSource?: 'chart' | 'third_party' | null;
  cashPrice: number;
  cashPricePerPerson?: number;
  cashPricingScope?: CashPricingScope;
  cashPassengerCount?: number;
  cashCabinClass?: string;
  currency?: string;
  cashComparison?: CashComparison;
  cabinClass: string;
  origin: string;
  destination: string;
  departureAt: string;
  arrivalAt: string;
  stops: number;
  totalDurationMinutes: number;
  stopovers?: Array<{ airportCode: string; airportName: string; layoverMinutes: number }>;
  returnSlice?: {
    origin: string;
    destination: string;
    departureAt: string;
    arrivalAt: string;
    duration: string;
    stops: number;
  };
  returnStopovers?: Array<{
    airportCode: string;
    airportName: string;
    layoverMinutes: number;
  }>;
  awardDecisionId?: string | null;
  awardDecisionSnapshot?: AwardDecisionSnapshot | null;
  isLocked?: boolean;
  awardAccess?: AwardAccess;
  awardPreviewLimit?: number | null;
  cpp?: number | null;
  cppDisplay?: string | null;
  cppRating?: string | null;
  redemptionScore?: number | null;
  hasTransferBonus?: boolean;
  transferBonus?: { bonusPercentage: number; effectiveTransfer: number } | null;
  recommendedPricing?: SaverPricingOption | null;
  pricingAlternatives?: SaverPricingOption[];
  alternateBookingPaths?: AlternateBookingPath[];
  pricingStatus?: PricingStatus;
  pricingQualifier?: PricingQualifier;
  compareRecommendation?: {
    isRecommended?: boolean;
    recommended?: boolean;
    confidence?: 'high' | 'medium' | 'low' | string | null;
    summary?: string | null;
  } | null;
  isCompareRecommended?: boolean;
  compareRecommendationConfidence?: 'high' | 'medium' | 'low' | string | null;
  isBestDeal?: boolean;
  cppBand?: 'poor' | 'fair' | 'good' | 'great' | string | null;
  scoreBand?: 'low' | 'medium' | 'high' | string | null;
  expiresAt?: string;
  // POINTS-209: per-slice segment context (marketing/operating carriers) preserved from the
  // search result so the compare request can carry it to the backend for program resolution.
  slices?: FlightSliceSummary[];
  // POINTS-277: explicit per-segment carrier contract for honest codeshare/mixed-operator
  // display in the compare tray. Absent ⇒ fall back to the offer-level fields above.
  carrierSegments?: FlightCarrierSegment[];
  // POINTS-189/190: top-level Duffel expiry freshness mirror for surfaces that read the flat shape.
  cashComparisonExpiresAt?: string;
  cashComparisonFreshness?: CashComparisonFreshness;
  cashComparisonRefreshRequired?: boolean;
}

export interface OutboundSearchResponse {
  offerRequestId: string;
  outbound: FlightOffer[];
  showUpgradeIntercept?: boolean;
}

export interface ReturnSearchResponse {
  offerRequestId: string;
  return: FlightOffer[];
}

export interface SelectFlightsResponse {
  outboundOfferId: string;
  returnOfferId?: string;
  deepLinkParams: Record<string, unknown>;
}

export interface TransferBonus {
  id: string;
  bankProgramId?: string;
  airlineProgramId: string;
  creditCardProgram?: string;
  transferPartner?: string;
  bonusPercentage: number;
  bonusPercent?: number;
  startDate: string;
  endDate: string;
  status: string;
  isActive?: boolean;
  sourceUrl?: string;
  // Admin projection (GET /admin/transfer-bonuses): effective status + raw
  // values + provenance. effectiveStatus/active are authoritative for display.
  effectiveStatus?: string;
  active?: boolean;
  rawStatus?: string;
  rawIsActive?: boolean;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ActiveTransferBonus {
  id: string;
  airlineProgramId?: string;
  creditCardProgram: string;
  transferPartner: string;
  // POINTS-273: stable program codes from the member endpoint (creditCardProgramId
  // = bankProgramId, transferPartnerId = airlineProgramId). Used for code-first matching.
  bankProgramId?: string;
  creditCardProgramId?: string;
  transferPartnerId?: string;
  bonusPercentage?: number;
  bonusPercent?: number;
  startDate: string;
  endDate: string;
  status?: string;
  isActive?: boolean;
  effectiveStatus?: string;
  active?: boolean;
  rawStatus?: string;
  rawIsActive?: boolean;
  sourceUrl?: string;
}

export interface SavedFlight {
  id: string;
  origin: string;
  destination: string;
  departureDate: string | null;
  returnDate: string | null;
  cabinClass: string | null;
  passengers: number | null;
  pointsRequired: number | null;
  loyaltyProgram: string | null;
  isBooked: boolean;
  // POINTS-296: optional cash-watch target. Amount is a decimal string to match
  // the backend contract; `null` on both fields means the cash watch is off.
  targetCashAmount: string | null;
  targetCurrency: string | null;
  savedAt: string;
  lastNotifiedAt: string | null;
}

export interface Notification {
  id: string;
  type: string; // 'system' | 'transfer_bonus' | 'saved_flight_cash_alert' | 'new_signup' | 'new_verified_signup'
  title: string;
  body: string;
  isRead: boolean;
  actionUrl: string | null;
  // POINTS-406: structured jsonb payload (PR #885). For new_signup:
  // { userId, email, name, createdAt, isVerified }; for new_verified_signup:
  // { userId, email, name, verifiedAt }. Null for older/other notification types.
  payload?: Record<string, unknown> | null;
  createdAt: string;
}

export interface SavedSearchInput {
  origin: string
  destination: string
  date: string
  passengers: number
  program: string
  cabinClass?: string
  returnDate?: string
}

export interface SavedSearch extends SavedSearchInput {
  id: string
  userId?: string
  createdAt: string
}

export type AdminBillingPlan = 'ATRIUM' | 'HERMES' | 'APOLLO';
export type AdminBillingInterval = 'monthly' | 'annual';

export interface PaymentCard {
  brand: string;
  last4: string;
  expMonth?: number | null;
  expYear?: number | null;
}

// Safe Stripe invoice summary returned on GET /payments/subscription/me
// (POINTS-399). Monetary amounts are Stripe minor units (cents).
export interface SubscriptionInvoice {
  id: string;
  date: string | null;
  paidAt: string | null;
  amount: number | null;
  amountPaid: number | null;
  amountDue: number | null;
  amountRefunded: number | null;
  currency: string | null;
  status: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface AdminBillingSummary {
  userId: string;
  currentPlan: AdminBillingPlan;
  subscriptionStatus: string | null;
  billingInterval: AdminBillingInterval | null;
  nextBillingDate: string | null;
  currentAmount: { amount: number; currency: string } | null;
  card: PaymentCard | null; // brand/last4 always present; exp optional
  invoices: Array<{
    id: string;
    date: string | null;
    amount: number | null;
    currency: string | null;
    status: string | null;
    hostedInvoiceUrl: string | null;
    invoicePdfUrl: string | null;
  }>;
}

export interface AdminSearchQuotaSummary {
  userId: string;
  email?: string | null;
  membershipTier: string | null;
  quotaLimit: number | null;
  quotaUsed: number | null;
  quotaRemaining: number | null;
  quotaDate: string | null;
  resetAt?: string | null;
  expiresAt?: string | null;
}

// Admin user record returned by the admin user CRUD endpoints. The backend sends
// a full user row; consumers here only rely on identity/profile fields, so extra
// server fields are captured by the index signature rather than typed as `any`.
export interface AdminUserResponse {
  id?: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  status?: string | null;
  membershipTier?: string | null;
  [key: string]: unknown;
}

// Analytics types
export interface AnalyticsFilter {
  startDate?: string
  endDate?: string
  tier?: string
}

export interface AnalyticsKpis {
  totalSearches: number; pointsUsed: number; activeSubscribers: number; pointsSavedBySaverAI: number
  totalSearchesChange: number; pointsUsedChange: number; activeSubscribersChange: number; pointsSavedChange: number
}

export interface SearchVolumeData {
  date: string; total: number; apollo: number; hermes: number; atrium: number
}

export interface SaverAIUsageData {
  daily: Array<{ date: string; saverAI: number; standard: number }>
  adoptionRate: number
}

export interface PopularRoute {
  origin: string; destination: string; searches: number; change: number
}

export interface TopSavingsRoute {
  origin: string; destination: string; avgSaved: number; totalSaved: number; percentSaved: number
}

export interface SubscriptionAnalytics {
  active: number; cancelled: number; mrr: number; newSignups: number; churned: number
  byTier: { apollo: number; hermes: number; atrium: number }
}

export interface LoyaltyAnalytics {
  totalPoints: number; avgBalance: number
  mostConnected: Array<{ programName: string; userCount: number; totalPoints: number }>
  mostUsedInSearches: Array<{ program: string; count: number }>
}

export interface GeographyAnalytics {
  totalRegions: number; topRegion: string
  byRegion: Array<{ country: string; users: number; percentage: number }>
}

export interface DeviceAnalytics {
  desktop: number; mobile: number; uniqueUsers: number; searchesPerUser: number; savedRoutes: number
}

export interface RecommendedAnalytics {
  conversionRate: number; saverAIAccuracy: number; arpu: number
  netSubscriberGrowth: number; tierChangeRate: number
}

// GET /admin/product-metrics response (POINTS-307).
// Mirrors the backend ProductMetrics return type in
// backend/src/admin/product-metrics.service.ts exactly. Fields are kept optional
// because the backend may omit nested blocks; raw counts are not nullable there.
export interface AdminProductMetricsUserMetric {
  users?: number | null;
  interactions?: number | null;
}

export interface AdminProductMetricsGeography {
  country?: string | null;
  region?: string | null;
  city?: string | null;
  users?: number | null;
  interactions?: number | null;
}

// POINTS-311: a single time-bucketed point in a trend series.
export interface AdminProductMetricsTrendPoint {
  date: string;
  count: number;
}

// POINTS-311: trend series keyed by funnel stage.
export interface AdminProductMetricsTrends {
  bucket?: 'day' | 'week' | null;
  series?: {
    searches?: AdminProductMetricsTrendPoint[] | null;
    saverAiSelected?: AdminProductMetricsTrendPoint[] | null;
    compareClicks?: AdminProductMetricsTrendPoint[] | null;
    flightReviewVisits?: AdminProductMetricsTrendPoint[] | null;
    bookingGuideOpens?: AdminProductMetricsTrendPoint[] | null;
    externalClicks?: AdminProductMetricsTrendPoint[] | null;
  } | null;
}

// POINTS-313: monthly vs annual plan counts split by paid tier.
export interface AdminProductMetricsPlanSplitTier {
  monthly?: number | null;
  annual?: number | null;
  unknown?: number | null;
}

export interface AdminProductMetrics {
  subscriptionHealth?: {
    totalUsers?: number | null;
    activePaidSubscribers?: number | null;
    tierMix?: { atrium?: number | null; hermes?: number | null; apollo?: number | null } | null;
    mrr?: number | null;
    arr?: number | null;
    revenueSource?: string | null;
    newPaidSubscribers?: number | null;
    upgrades?: number | null;
    downgrades?: number | null;
    cancellations?: number | null;
    failedPayments?: number | null;
    recoveredPayments?: number | null;
    planSplit?: { monthly?: number | null; annual?: number | null; unknown?: number | null } | null;
    // POINTS-313: monthly/annual split broken out per paid tier.
    planSplitByTier?: {
      hermes?: AdminProductMetricsPlanSplitTier | null;
      apollo?: AdminProductMetricsPlanSplitTier | null;
    } | null;
    stripeWebhookHealth?: {
      lastReceivedAt?: string | null;
      successfulDeliveries?: number | null;
      failedDeliveries?: number | null;
    } | null;
  } | null;
  productFunnel?: {
    search?: {
      users?: number | null;
      clicks?: number | null;
      successfulSearches?: number | null;
      failedSearches?: number | null;
      noResultSearches?: number | null;
    } | null;
    saverAi?: {
      selectedUsers?: number | null;
      selectedClicks?: number | null;
      followedThroughClicks?: number | null;
      selectionRate?: number | null;
    } | null;
    compare?: {
      users?: number | null;
      clicks?: number | null;
      toFlightReviewUsers?: number | null;
      toFlightReviewClicks?: number | null;
    } | null;
    flightReview?: {
      users?: number | null;
      visits?: number | null;
      toBookingGuideUsers?: number | null;
      toBookingGuideClicks?: number | null;
    } | null;
    bookingGuide?: {
      users?: number | null;
      opens?: number | null;
      externalAirlineProgramClickThroughUsers?: number | null;
      externalAirlineProgramClickThroughClicks?: number | null;
      transferCalculatorClickThroughUsers?: number | null;
      transferCalculatorClickThroughClicks?: number | null;
      externalCtr?: number | null;
      transferCalculatorCtr?: number | null;
    } | null;
  } | null;
  engagementAndAlerts?: {
    savedFlightUsers?: number | null;
    savedFlights?: number | null;
    alertUsers?: number | null;
    alerts?: number | null;
    transferBonusUsers?: number | null;
    transferBonusClicks?: number | null;
  } | null;
  // Record keyed by device type (desktop | mobile | tablet | unknown).
  deviceMetrics?: Record<string, AdminProductMetricsUserMetric> | null;
  geographicMetrics?: AdminProductMetricsGeography[] | null;
  // POINTS-311: time-bucketed trend series for key funnel stages.
  trends?: AdminProductMetricsTrends | null;
}

export interface AdminProductMetricsDateRange {
  startDate: string;
  endDate: string;
}

// POINTS-312: GET /admin/analytics/email-engagement response (POINTS-305 backend).
export interface AdminEmailEngagementRow {
  alertType: string;
  delivered?: number | null;
  opened?: number | null;
  clicked?: number | null;
  openRate?: number | null;
  clickRate?: number | null;
}

export interface AdminEmailEngagement {
  openRateIsApproximate?: boolean | null;
  note?: string | null;
  byAlertType?: AdminEmailEngagementRow[] | null;
  overTime?: Array<Record<string, unknown>> | null;
}

export interface AdminEmailEngagementQuery {
  startDate: string;
  endDate: string;
  alertType?: string;
}

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('accessToken');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_URL}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const body = await response.text();
    if (!body) {
      return undefined as T;
    }

    return JSON.parse(body) as T;
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  // Auth endpoints
  async login(credentials: Omit<LoginRequest, 'deviceToken'>): Promise<LoginResponse> {
    const deviceToken = localStorage.getItem('otp_device_token') ?? undefined;
    const response = await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ ...credentials, deviceToken }),
    });
    if ('accessToken' in response) {
      this.setToken(response.accessToken);
    }
    return response;
  }

  async sendOtp(otpToken: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/2fa/send-otp', {
      method: 'POST',
      body: JSON.stringify({ otpToken }),
    });
  }

  async verifyOtp(data: { otpToken: string; code: string; rememberMe?: boolean }): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/2fa/verify-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(response.accessToken);
    return response;
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(response.accessToken);
    return response;
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/resend-verification', { method: 'POST' });
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async getProfile(): Promise<UserProfile> {
    const profile = await this.request<UserProfile>('/auth/profile', { method: 'GET' });
    // Avatar comes back as a relative proxy path (/users/:id/avatar); make it absolute for <img>.
    if (profile.avatar && profile.avatar.startsWith('/')) {
      profile.avatar = `${API_URL}${profile.avatar}`;
    }
    return profile;
  }

  async updateProfile(userId: string, data: Partial<{ firstName: string; lastName: string; phoneNumber: string; dateOfBirth: string }>): Promise<UserProfile> {
    return this.request<UserProfile>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);

    // Do NOT set Content-Type — the browser must set the multipart boundary itself.
    const headers: Record<string, string> = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const response = await fetch(`${API_URL}/users/me/avatar`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to upload avatar' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    const data = (await response.json()) as { avatarUrl: string };
    if (data.avatarUrl && data.avatarUrl.startsWith('/')) {
      data.avatarUrl = `${API_URL}${data.avatarUrl}`;
    }
    return data;
  }

  logout() {
    this.setToken(null);
  }

  // Flight endpoints
  async searchFlightsV3(body: SearchRequestBody): Promise<FlightSearchResponse> {
    const url = `${API_URL}/flights/search`;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), FLIGHT_SEARCH_TIMEOUT_MS);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      // POINTS-46: backend scopes Redis quota key to user's local date using this header
      'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers,
        signal: controller.signal,
      });

      if (!response.ok) {
        const rawError = await response.text().catch(() => '');
        let message = rawError || response.statusText || 'Search request failed';
        try {
          const parsed = JSON.parse(rawError) as { message?: string | string[]; error?: string };
          const parsedMessage = Array.isArray(parsed.message) ? parsed.message.join('; ') : parsed.message;
          message = parsedMessage || parsed.error || message;
        } catch {
          // Plain-text error body; keep the raw message.
        }

        throw new Error(`Search request failed (${response.status} ${response.statusText}): ${message}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Search request failed')) {
        throw error;
      }

      const errorName =
        error && typeof error === 'object' && 'name' in error
          ? String((error as { name?: unknown }).name)
          : null;
      const errorMessage =
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message)
          : String(error);
      const isTimeout = errorName === 'AbortError';
      console.error('Search request failed before response', {
        endpoint: '/flights/search',
        url,
        routeParams: {
          origin: body.origin,
          destination: body.destination,
          departureDate: body.departureDate,
          returnDate: body.returnDate,
          cabin: body.cabin,
          passengers: body.passengers,
        },
        hasAuthHeader: Boolean(headers.Authorization),
        timeoutMs: FLIGHT_SEARCH_TIMEOUT_MS,
        failureType: isTimeout ? 'timeout' : 'fetch',
        errorName,
        errorMessage,
      });

      if (isTimeout) {
        throw new Error(`Search request timed out after ${FLIGHT_SEARCH_TIMEOUT_MS / 1000} seconds.`);
      }

      throw new Error(`Search request failed before response: ${errorMessage}`);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  async getSearchQuota(): Promise<{ used: number | null; limit: number | null; resetsAt: string | null }> {
    return this.request('/flights/search-quota', {
      headers: {
        // POINTS-46: backend scopes Redis quota key to user's local date using this header
        'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }

  async exportFlightsCsv(params: { origin: string; destination: string; departureDate: string; cabin?: string }): Promise<Blob> {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const url = `${API_URL}/flights/export?${query}`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  async saveSearch(data: SavedSearchInput): Promise<SavedSearch> {
    return this.request<SavedSearch>('/searches', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getRecentSearches(): Promise<SavedSearch[]> {
    return this.request<SavedSearch[]>('/searches')
  }

  // Loyalty balance endpoints
  async getLoyaltyTotalBalance(): Promise<{ totalBalance: number }> {
    return this.request('/loyalty/total-balance');
  }

  // Canonical airline loyalty programs (POINTS-302) — powers the saved-flight
  // transfer-bonus watch program picker. Sorted by programName server-side.
  async getAirlineLoyaltyPrograms(): Promise<{ programName: string; programCode: string | null }[]> {
    return this.request('/loyalty/airline-programs');
  }

  // Booking endpoints
  async createBooking(bookingData: unknown): Promise<unknown> {
    return this.request<unknown>('/bookings', { method: 'POST', body: JSON.stringify(bookingData) });
  }

  async getBookings(): Promise<unknown> {
    return this.request<unknown>('/bookings', { method: 'GET' });
  }

  // Subscription / Payment endpoints
  async createCheckout(plan: string, interval: string): Promise<{ checkoutUrl: string }> {
    return this.request<{ checkoutUrl: string }>('/payments/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, interval }),
    });
  }

  async verifySubscription(sessionId: string): Promise<{ success: boolean; tier?: string; user?: UserProfile }> {
    return this.request<{ success: boolean; tier?: string; user?: UserProfile }>('/payments/subscription/verify', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  async getMySubscription(): Promise<{ tier: string; status: string; stripePriceId: string | null; billingInterval: 'monthly' | 'annual' | null; currentPeriodEnd: string | null; card: PaymentCard | null; invoices: SubscriptionInvoice[] } | null> {
    return this.request('/payments/subscription/me', { method: 'GET' });
  }

  async refreshToken(): Promise<{ token: string; membershipTier: string; role: string }> {
    return this.request<{ token: string; membershipTier: string; role: string }>('/payments/refresh-token');
  }

  async openPortal(): Promise<{ portalUrl: string }> {
    return this.request<{ portalUrl: string }>('/payments/portal', { method: 'POST' });
  }

  async getMyPayments(): Promise<unknown[]> {
    return this.request<unknown[]>('/payments/my-payments', { method: 'GET' });
  }

  async findNearestAirport(lat: number, lng: number): Promise<{ iata: string; name: string; city: string; country: string } | null> {
    try {
      return await this.request<{ iata: string; name: string; city: string; country: string } | null>(
        `/airports/nearest?lat=${lat}&lng=${lng}`,
      );
    } catch {
      return null;
    }
  }

  async searchAirports(query: string): Promise<AirportSearchResult[]> {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    return this.request<AirportSearchResult[]>(
      `/airports/search?q=${encodeURIComponent(trimmed)}`,
      { method: 'GET' },
    );
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async submitContact(data: { firstName: string; lastName: string; email: string; subject: string; message: string }): Promise<{ message: string }> {
    return this.request<{ message: string }>('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePreferences(userId: string, preferences: Record<string, unknown>): Promise<UserProfile> {
    return this.request<UserProfile>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  }

  // POINTS theme preference (#408). Sends the backend `themePreference` payload directly.
  async updateThemePreference(themePreference: 'light' | 'dark'): Promise<{ themePreference: 'light' | 'dark' }> {
    return this.request<{ themePreference: 'light' | 'dark' }>('/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ themePreference }),
    });
  }

  async deleteAccount(userId: string): Promise<void> {
    return this.request<void>(`/users/${userId}`, { method: 'DELETE' });
  }

  // Admin endpoints
  async getAdminDashboardStats(): Promise<{
    totalUsers: number;
    activeUsers: number;
    totalBookings: number;
    totalRevenue: number;
    monthlyRevenue: number;
    monthlySearches: number;
    conversionRate: number;
    subscriptionBreakdown: { atrium: number; hermes: number; apollo: number };
    recentActivity: { users: number; bookings: number; revenue: number };
  }> {
    return this.request('/admin/dashboard');
  }

  async getAdminProductMetrics({ startDate, endDate }: AdminProductMetricsDateRange): Promise<AdminProductMetrics> {
    const params = new URLSearchParams({ startDate, endDate });
    // Send the viewer's timezone so the YYYY-MM-DD day range is bounded in their
    // local day, not UTC (otherwise evening activity falls into the next UTC day
    // and drops out of "today").
    return this.request(`/admin/product-metrics?${params.toString()}`, {
      headers: { 'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
  }

  // POINTS-312: email engagement analytics (POINTS-305 backend endpoint).
  async getEmailEngagement({ startDate, endDate, alertType }: AdminEmailEngagementQuery): Promise<AdminEmailEngagement> {
    const params = new URLSearchParams({ startDate, endDate });
    if (alertType) params.set('alertType', alertType);
    return this.request(`/admin/analytics/email-engagement?${params.toString()}`);
  }

  async getAdminUsers(): Promise<unknown[]> {
    return this.request('/admin/users');
  }

  // POINTS-407: admins-only feed of the most recently email-verified users
  // (backend excludes emailVerifiedAt=null, sorts DESC, default 10, max 50).
  async getRecentlyVerifiedUsers(limit = 10): Promise<AdminUserResponse[]> {
    return this.request(`/admin/users/recently-verified?limit=${limit}`);
  }

  async getAdminBookings(): Promise<unknown[]> {
    return this.request('/admin/bookings');
  }

  async getAdminPayments(): Promise<unknown[]> {
    return this.request('/admin/payments');
  }

  async getAdminRecentBookings(limit?: number): Promise<unknown[]> {
    const query = limit ? `?limit=${limit}` : '';
    return this.request(`/admin/bookings/recent${query}`);
  }

  async updateAdminUserStatus(id: string, status: 'ACTIVE' | 'SUSPENDED'): Promise<AdminUserResponse> {
    const path = status === 'SUSPENDED' ? 'suspend' : 'activate';
    return this.request(`/admin/users/${id}/${path}`, { method: 'PATCH' });
  }

  // POINTS-406: admin-triggered resend of a pending user's verification email.
  // Backend rejects an already-verified user (400) and enforces a 60s per-user
  // cooldown (429 "Verification email was sent recently").
  async resendUserVerification(id: string): Promise<{ message: string }> {
    return this.request(`/admin/users/${id}/resend-verification`, { method: 'POST' });
  }

  async createAdminUser(data: { email: string; password: string; role?: string; membershipTier?: string }): Promise<AdminUserResponse> {
    return this.request('/admin/users', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateAdminUser(id: string, data: { password?: string; membershipTier?: string; role?: string; firstName?: string; lastName?: string }): Promise<AdminUserResponse> {
    return this.request(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteAdminUser(id: string): Promise<void> {
    return this.request(`/admin/users/${id}`, { method: 'DELETE' });
  }

  async getAdminUserSearchQuota(userId: string): Promise<AdminSearchQuotaSummary> {
    return this.request(`/admin/users/${userId}/search-quota`, {
      method: 'GET',
      headers: { 'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
  }

  async resetAdminUserSearchQuota(userId: string): Promise<AdminSearchQuotaSummary> {
    return this.request(`/admin/users/${userId}/search-quota/reset`, {
      method: 'POST',
      headers: { 'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
  }

  async getAdminBillingSummary(userId: string): Promise<AdminBillingSummary> {
    return this.request(`/admin/billing/${userId}`);
  }

  async changeAdminBillingPlan(
    userId: string,
    data: { plan: AdminBillingPlan; interval?: AdminBillingInterval },
  ): Promise<unknown> {
    return this.request(`/admin/billing/${userId}/change-plan`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelAdminBilling(userId: string): Promise<unknown> {
    return this.request(`/admin/billing/${userId}/cancel`, { method: 'POST' });
  }

  async refundAdminBillingLastInvoice(userId: string): Promise<unknown> {
    return this.request(`/admin/billing/${userId}/refund`, { method: 'POST' });
  }

  // Loyalty endpoints
  async getLoyaltyPrograms(): Promise<{ id: string; name: string; programId: string }[]> {
    return this.request('/loyalty/programs');
  }

  async getLoyaltyAccounts(): Promise<unknown[]> {
    return this.request('/loyalty/accounts');
  }

  async addLoyaltyAccount(data: { programId: string; accountNumber: string; balance: number }): Promise<unknown> {
    return this.request('/loyalty/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteLoyaltyAccount(accountId: string): Promise<void> {
    return this.request(`/loyalty/accounts/${accountId}`, { method: 'DELETE' });
  }

  // AwardWallet Account Access API
  async connectAwardWallet(): Promise<{ connectUrl: string }> {
    return this.request('/awardwallet/connect');
  }

  async getAwardWalletAccounts(): Promise<unknown[]> {
    return this.request('/awardwallet/accounts');
  }

  async syncAwardWallet(): Promise<{ synced: boolean }> {
    return this.request('/awardwallet/sync', { method: 'POST' });
  }

  // Credit card loyalty programs
  async getAvailableLoyaltyPrograms(): Promise<string[]> {
    return this.request('/loyalty/programs/available');
  }

  async getUserCreditCardPrograms(): Promise<{ id: string; programName: string; isFavorited: boolean; createdAt: string }[]> {
    return this.request('/loyalty/programs');
  }

  async addCreditCardProgram(programName: string): Promise<{ id: string; programName: string; isFavorited: boolean; createdAt: string }> {
    return this.request('/loyalty/programs', { method: 'POST', body: JSON.stringify({ programName }) });
  }

  async deleteCreditCardProgram(id: string): Promise<void> {
    return this.request(`/loyalty/programs/${id}`, { method: 'DELETE' });
  }

  async toggleProgramFavorite(id: string): Promise<{ id: string; programName: string; isFavorited: boolean; createdAt: string }> {
    return this.request(`/loyalty/programs/${id}/favorite`, { method: 'PATCH' });
  }

  async getTransferPartners(program: string): Promise<{ airline: string; transferRatio: string; loyaltyProgramCode?: string | null }[]> {
    return this.request(`/loyalty/transfer-partners?program=${encodeURIComponent(program)}`);
  }

  async getActiveTransferBonuses(): Promise<ActiveTransferBonus[]> {
    return this.request('/transfer-bonuses/active').catch(() => []);
  }

  // Saved Flights
  async getSavedFlights(): Promise<SavedFlight[]> {
    return this.request('/saved-flights');
  }

  // POINTS-296: SavedFlights is the single create + manage surface for Price &
  // Transfer Alerts. The backend `POST /saved-flights` is extended to accept an
  // optional cash target (decimal-string amount + currency). Round-trip sends
  // `returnDate`; one-way omits it.
  async createSavedFlight(data: {
    origin: string;
    destination: string;
    departureDate?: string;
    returnDate?: string;
    cabinClass?: string;
    passengers?: number;
    pointsRequired?: number;
    loyaltyProgram?: string;
    targetCashAmount?: string | null;
    targetCurrency?: string | null;
  }): Promise<SavedFlight & { alreadyTracked: boolean }> {
    return this.request('/saved-flights', { method: 'POST', body: JSON.stringify(data) });
  }

  // POINTS-296: manage a saved alert. `isBooked` flips booking state; setting a
  // cash target sends `targetCashAmount` (decimal string) + `targetCurrency`.
  // Option A unwatch = PATCH `{ targetCashAmount: null, targetCurrency: null }`.
  async updateSavedFlight(
    id: string,
    data: { isBooked?: boolean; targetCashAmount?: string | null; targetCurrency?: string | null },
  ): Promise<SavedFlight> {
    return this.request(`/saved-flights/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteSavedFlight(id: string): Promise<void> {
    return this.request(`/saved-flights/${id}`, { method: 'DELETE' });
  }

  // Notification endpoints
  async getNotifications(): Promise<Notification[]> {
    return this.request<Notification[]>('/notifications');
  }

  async getUnreadCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  async markNotificationRead(id: string): Promise<void> {
    return this.request<void>(`/notifications/mark-read/${id}`, { method: 'POST' });
  }

  async markAllNotificationsRead(): Promise<void> {
    return this.request<void>('/notifications/mark-all-read', { method: 'POST' });
  }

  // Admin transfer bonus CRUD
  async getAdminTransferBonuses(): Promise<TransferBonus[]> {
    return this.request('/admin/transfer-bonuses');
  }

  async createAdminTransferBonus(dto: Partial<TransferBonus>): Promise<TransferBonus> {
    return this.request('/admin/transfer-bonuses', { method: 'POST', body: JSON.stringify(dto) });
  }

  async updateAdminTransferBonus(id: string, dto: Partial<TransferBonus>): Promise<TransferBonus> {
    return this.request(`/admin/transfer-bonuses/${id}`, { method: 'PATCH', body: JSON.stringify(dto) });
  }

  async deleteAdminTransferBonus(id: string): Promise<void> {
    return this.request(`/admin/transfer-bonuses/${id}`, { method: 'DELETE' });
  }

  async getPendingTransferBonuses(): Promise<TransferBonus[]> {
    return this.request('/admin/transfer-bonuses?status=PENDING_REVIEW');
  }

  async publishTransferBonus(id: string): Promise<TransferBonus> {
    return this.request(`/admin/transfer-bonuses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'ACTIVE', isActive: true }),
    });
  }

  async rejectTransferBonus(id: string): Promise<TransferBonus> {
    return this.request(`/admin/transfer-bonuses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'REJECTED' }),
    });
  }

  async updateTransferBonus(id: string, data: Partial<TransferBonus>): Promise<TransferBonus> {
    return this.request(`/admin/transfer-bonuses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Analytics helper: build query string from filter
  private analyticsQuery(filter?: AnalyticsFilter): string {
    if (!filter) return ''
    const p = new URLSearchParams()
    if (filter.startDate) p.set('startDate', filter.startDate)
    if (filter.endDate) p.set('endDate', filter.endDate)
    if (filter.tier) p.set('tier', filter.tier)
    const qs = p.toString()
    return qs ? `?${qs}` : ''
  }

  async getAnalyticsKpis(filter?: AnalyticsFilter): Promise<AnalyticsKpis> {
    return this.request<AnalyticsKpis>(`/admin/analytics/kpis${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsSearchVolume(filter?: AnalyticsFilter): Promise<SearchVolumeData[]> {
    return this.request<SearchVolumeData[]>(`/admin/analytics/search-volume${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsSaverAIUsage(filter?: AnalyticsFilter): Promise<SaverAIUsageData> {
    return this.request<SaverAIUsageData>(`/admin/analytics/saverai-usage${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsPopularRoutes(filter?: AnalyticsFilter): Promise<PopularRoute[]> {
    return this.request<PopularRoute[]>(`/admin/analytics/popular-routes${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsTopSavingsRoutes(filter?: AnalyticsFilter): Promise<TopSavingsRoute[]> {
    return this.request<TopSavingsRoute[]>(`/admin/analytics/top-savings-routes${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsSubscriptions(filter?: AnalyticsFilter): Promise<SubscriptionAnalytics> {
    return this.request<SubscriptionAnalytics>(`/admin/analytics/subscriptions${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsLoyalty(filter?: AnalyticsFilter): Promise<LoyaltyAnalytics> {
    return this.request<LoyaltyAnalytics>(`/admin/analytics/loyalty${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsGeography(filter?: AnalyticsFilter): Promise<GeographyAnalytics> {
    return this.request<GeographyAnalytics>(`/admin/analytics/geography${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsDevices(filter?: AnalyticsFilter): Promise<DeviceAnalytics> {
    return this.request<DeviceAnalytics>(`/admin/analytics/devices${this.analyticsQuery(filter)}`)
  }
  async getAnalyticsRecommended(filter?: AnalyticsFilter): Promise<RecommendedAnalytics> {
    return this.request<RecommendedAnalytics>(`/admin/analytics/recommended${this.analyticsQuery(filter)}`)
  }

  // POINTS-82: two-step flight search endpoints
  async searchOutbound(body: {
    origin: string;
    destination: string;
    departureDate: string;
    cabinClass: string;
    passengers: number;
    programCode?: string;
  }): Promise<OutboundSearchResponse> {
    return this.request<OutboundSearchResponse>('/flights/search/outbound', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
  }

  async searchReturn(body: {
    origin: string;
    destination: string;
    returnDate: string;
    cabinClass: string;
    passengers: number;
    programCode?: string;
    selectedOutboundOfferId: string;
    originalOfferRequestId: string;
  }): Promise<ReturnSearchResponse> {
    return this.request<ReturnSearchResponse>('/flights/search/return', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
  }

  async selectFlights(body: {
    outboundOfferId: string;
    returnOfferId?: string;
  }): Promise<SelectFlightsResponse> {
    return this.request<SelectFlightsResponse>('/flights/search/select', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async compareFlights(body: CompareRequestBody): Promise<RouteComparisonResponse> {
    const url = `${API_URL}/flights/compare`;
    const serializedBody = JSON.stringify(body);
    const payloadSizeBytes =
      typeof TextEncoder !== 'undefined'
        ? new TextEncoder().encode(serializedBody).length
        : serializedBody.length;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: serializedBody,
        headers,
      });

      if (!response.ok) {
        const rawError = await response.text().catch(() => '');
        let message = rawError || response.statusText || 'Compare request failed';
        try {
          const parsed = JSON.parse(rawError) as { message?: string | string[]; error?: string };
          const parsedMessage = Array.isArray(parsed.message) ? parsed.message.join('; ') : parsed.message;
          message = parsedMessage || parsed.error || message;
        } catch {
          // Plain-text error body; keep the raw message.
        }

        throw new Error(`Compare request failed (${response.status} ${response.statusText}): ${message}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Compare request failed')) {
        throw error;
      }

      const errorName =
        error && typeof error === 'object' && 'name' in error
          ? String((error as { name?: unknown }).name)
          : null;
      const errorMessage =
        error instanceof Error
          ? error.message
          : error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message)
          : String(error);

      console.error('Compare request failed before response', {
        endpoint: '/flights/compare',
        offerCount: body.offers.length,
        offerIds: body.offers.map((offer) => offer.offerId).filter(Boolean),
        payloadSizeBytes,
        hasAuthHeader: Boolean(headers.Authorization),
        routeContext: body.routeContext,
        errorName,
        errorMessage,
      });

      throw new Error(`Compare request failed before response: ${errorMessage}`);
    }
  }

  async searchFlightsOverview(body: SearchOverviewRequestBody): Promise<SearchOverviewApiResponse> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(`${API_URL}/flights/search/overview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
          'X-User-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (response.status === 401) {
        throw new Error('401 Unauthorized');
      }

      if (response.status === 403) {
        return null;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
        throw new Error(error.message || `HTTP error! status: ${response.status}`);
      }

      const payload = (await response.json().catch(() => null)) as SearchOverviewApiResponse;
      return payload;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null;
      }

      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async querySaverAI(params: {
    origin: string;
    destination: string;
    airline: string;
    cabin: string;
    creditCardProgram?: string;
  }): Promise<{
    milesRequired?: number;
    confidenceTier?: string;
    label?: string;
    insight?: string;
    cppDisplay?: string;
    cppRating?: string;
  }> {
    return this.request('/saverai/route', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // POINTS-332/334: public blog list. Optional category filter + pagination.
  // Degrades to readable empty state until the blog backend ships.
  async getBlogPosts(query: BlogListQuery = {}): Promise<BlogPostListResponse> {
    const params = new URLSearchParams();
    if (query.category) params.set('category', query.category);
    if (query.page != null) params.set('page', String(query.page));
    if (query.pageSize != null) params.set('pageSize', String(query.pageSize));
    const qs = params.toString();
    return this.request<BlogPostListResponse>(`/blog/posts${qs ? `?${qs}` : ''}`);
  }

  // Stable per-browser id so anonymous visitors can react once per post and see
  // their own vote (`mine`). The blog backend reads it via the `x-blog-session`
  // header; without it anonymous reactions are rejected with a 400.
  private blogSessionId(): string {
    if (typeof window === 'undefined' || !window.localStorage) return 'anon';
    const KEY = 'pp_blog_session';
    let id = window.localStorage.getItem(KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(KEY, id);
    }
    return id;
  }

  // POINTS-334: public post detail by slug. Carries body + reactions.
  // Sends the blog session so `mine` resolves for logged-out readers.
  async getBlogPost(slug: string): Promise<BlogPostDetail> {
    return this.request<BlogPostDetail>(`/blog/posts/${encodeURIComponent(slug)}`, {
      headers: { 'x-blog-session': this.blogSessionId() },
    });
  }

  // POINTS-337: thumbs up/down on a post. Returns the fresh counts + viewer state.
  async reactToBlogPost(postId: string, value: BlogReactionValue): Promise<BlogReactions> {
    return this.request<BlogReactions>(`/blog/posts/${encodeURIComponent(postId)}/react`, {
      method: 'POST',
      body: JSON.stringify({ value }),
      headers: { 'x-blog-session': this.blogSessionId() },
    });
  }

  // POINTS-335: newsletter subscribe. `source` distinguishes the blog CTA from
  // the settings surface so admin metrics can split signups by origin.
  async subscribeToBlog(email: string, source: BlogSubscribeSource): Promise<BlogSubscribeResponse> {
    return this.request<BlogSubscribeResponse>('/blog/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email, source }),
    });
  }

  // POINTS-333: admin blog list (drafts + published).
  async getAdminBlogPosts(): Promise<AdminBlogPost[]> {
    return this.request<AdminBlogPost[]>('/admin/blog/posts');
  }

  async createAdminBlogPost(data: AdminBlogPostInput): Promise<AdminBlogPost> {
    return this.request<AdminBlogPost>('/admin/blog/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminBlogPost(id: string, data: Partial<AdminBlogPostInput>): Promise<AdminBlogPost> {
    return this.request<AdminBlogPost>(`/admin/blog/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async publishAdminBlogPost(id: string): Promise<AdminBlogPost> {
    return this.request<AdminBlogPost>(`/admin/blog/posts/${id}/publish`, { method: 'POST' });
  }

  async deleteAdminBlogPost(id: string): Promise<void> {
    return this.request<void>(`/admin/blog/posts/${id}`, { method: 'DELETE' });
  }

  // POINTS-344: backfill SaverAI "Key Takeaways" summaries for published posts
  // that are missing one (idempotent — already-summarized posts are skipped).
  // Pass force=true to regenerate every published post's summary.
  async backfillAdminBlogSummaries(force = false): Promise<AdminBlogBackfillResult> {
    const qs = force ? '?force=true' : '';
    return this.request<AdminBlogBackfillResult>(
      `/admin/blog/posts/backfill-summaries${qs}`,
      { method: 'POST' },
    );
  }

  // POINTS-344: force-regenerate a single post's SaverAI summary.
  async regenerateAdminBlogSummary(id: string): Promise<AdminBlogRegenerateResult> {
    return this.request<AdminBlogRegenerateResult>(
      `/admin/blog/posts/${id}/regenerate-summary`,
      { method: 'POST' },
    );
  }

  // POINTS-338: blog metrics (subscribers, published counts, per-post engagement).
  async getAdminBlogMetrics(query: AdminBlogMetricsQuery = {}): Promise<AdminBlogMetrics> {
    const params = new URLSearchParams();
    if (query.startDate) params.set('startDate', query.startDate);
    if (query.endDate) params.set('endDate', query.endDate);
    const qs = params.toString();
    return this.request<AdminBlogMetrics>(`/admin/blog/metrics${qs ? `?${qs}` : ''}`);
  }

  // POINTS-307: fire-and-forget product analytics emit. Never throws into UX —
  // analytics must never break a user flow. Swallows network/auth/validation
  // errors and only logs in dev.
  async recordProductAnalyticsAction(
    action: ProductAnalyticsAction,
    opts: RecordProductAnalyticsActionOptions = {},
  ): Promise<void> {
    try {
      await this.request('/product-analytics/actions', {
        method: 'POST',
        body: JSON.stringify({
          action,
          surface: opts.surface,
          metadata: opts.metadata,
        }),
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('Failed to record product analytics action', action, error);
      }
    }
  }
}

export const api = new ApiService();

export const handleAuthError = (error: unknown) => {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? String((error as { message?: unknown }).message)
      : '';
  if (message.includes('401') || message.includes('Unauthorized')) {
    api.logout();
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('user');
    toast.error('Session expired. Please login again.');
    window.location.href = '/auth?mode=signin';
  }
  throw error;
};
